"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion } from "framer-motion";
import { CircleCheck as CheckCircle2, ShieldCheck, UserCheck, ArrowRight, Loader as Loader2, Calendar } from 'lucide-react';
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const guarantees = [{
  text: "100% Direct Access — You work directly with me, Yassine Essaadi."
}, {
  text: "No Junior Account Managers — Expert execution and strategic clarity."
}, {
  text: "Tailored SEO Roadmap — Transparent reporting and real business impact."
}];
function ConsultationFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-muted/50 rounded-xl border border-border text-center space-y-4">
        <div className="inline-flex items-center justify-center size-12 rounded-full bg-accent/20 text-accent mx-auto">
          <CheckCircle2 className="size-6 text-accent" />
        </div>
        <h3 className="text-xl font-bold font-default text-foreground">Consultation Requested</h3>
        <p className="text-sm text-muted-foreground font-default">
          {successMessage || "Thank you! I will personally review your website details and reach out within 24 hours to confirm our strategy call."}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-2 text-foreground border-border hover:bg-muted">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <FormField control={control} name="name" rules={{
      required: "Your name is required",
      minLength: {
        value: 2,
        message: "Please enter at least 2 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-default text-sm font-medium">Your Name</FormLabel>
            <FormControl>
              <Input placeholder="e.g. Sarah Jenkins" className="bg-background text-foreground border-input px-3 py-2 font-default text-sm rounded-md" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email address is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-default text-sm font-medium">Work Email</FormLabel>
            <FormControl>
              <Input type="email" placeholder="sarah@company.com" className="bg-background text-foreground border-input px-3 py-2 font-default text-sm rounded-md" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="website" rules={{
      required: "Website URL is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-default text-sm font-medium">Website URL</FormLabel>
            <FormControl>
              <Input type="text" placeholder="https://yourwebsite.com" className="bg-background text-foreground border-input px-3 py-2 font-default text-sm rounded-md" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
          <p className="text-destructive text-sm font-default">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full bg-accent text-accent-foreground hover:bg-accent/90 font-medium h-11 px-6 rounded-md transition-[background-color,box-shadow] shadow-md cursor-pointer text-base">
        {isSubmitting ? <>
            <Loader2 className="animate-spin size-4 mr-2" />
            Scheduling Call...
          </> : <>
            Book a Free SEO Consultation
            <ArrowRight className="size-4 ml-1" />
          </>}
      </Button>

      <p className="text-xs text-center text-muted-foreground font-default pt-2">
        Direct 1-on-1 advice. No high-pressure sales pitch.
      </p>
    </Form>;
}
export default function Consultation() {
  return <section id="consultationcta" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }} className="lg:col-span-7 space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-semibold tracking-wide uppercase font-default">
              <UserCheck className="size-4 text-primary" />
              <span>Independent SEO Expertise</span>
            </div>

            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-default text-foreground tracking-tight leading-tight">
              Ready for Sustainable Search Growth?
            </h2>

            <p className="text-base sm:text-lg text-muted-foreground font-default leading-relaxed max-w-2xl">
              When you choose YE Consultancy, you don't get handed off to junior account managers. You work directly with me, Yassine Essaadi, to audit, craft, and execute a bespoke SEO strategy that drives real revenue.
            </p>

            <div className="space-y-3 pt-2">
              {guarantees.map((item, i) => <div data-index={i} key={i} className="flex items-start gap-3">
                  <div className="p-1 rounded-full bg-accent/15 text-accent shrink-0 mt-0.5">
                    <CheckCircle2 className="size-4 text-accent" />
                  </div>
                  <span className="text-sm sm:text-base font-default text-foreground font-medium">
                    {item.text}
                  </span>
                </div>)}
            </div>

            <div className="pt-4 flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-3 p-4 rounded-xl bg-card border border-border shadow-xs">
                <div className="p-2.5 rounded-lg bg-primary text-primary-foreground">
                  <SafeHydrate><Calendar className="size-5" /></SafeHydrate>
                </div>
                <div>
                  <h3 className="text-sm font-semibold font-default text-foreground">30-Minute Strategy Session</h3>
                  <p className="text-xs text-muted-foreground font-default">Free custom review of your search performance</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <ShieldCheck className="size-5 text-primary" />
                <span className="text-xs text-muted-foreground font-default">
                  Strictly Confidential & Confidentiality Assured
                </span>
              </div>
            </div>

            <div className="pt-2">
              <span className="text-sm text-muted-foreground font-default">
                Prefer to write directly?{" "}
                <Link to="/ContactPage" className="text-primary hover:underline font-medium">
                  Visit my Contact page
                </Link>
              </span>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1,
          ease: "easeOut"
        }} className="lg:col-span-5">
            <div className="bg-card text-card-foreground border border-border rounded-2xl p-6 sm:p-8 shadow-xl">
              <div className="mb-6 space-y-1">
                <h3 className="text-xl font-bold font-default text-foreground">
                  Book Your Consultation
                </h3>
                <p className="text-sm text-muted-foreground font-default">
                  Fill in your details below and I'll prepare preliminary insights for our session.
                </p>
              </div>

              <FormProvider formId="consultation-cta-form" sectionName="consultationcta" defaultValues={{
              name: "",
              email: "",
              website: ""
            }} validationRules={{
              name: {
                required: true,
                minLength: 2
              },
              email: {
                required: true,
                pattern: "email"
              },
              website: {
                required: true
              }
            }} submitText="Book a Free SEO Consultation" successMessage="Thank you! I will personally review your website details and reach out within 24 hours to confirm our call.">
                <ConsultationFormContent />
              </FormProvider>
            </div>
          </motion.div>

        </div>
      </div>
    </section>;
}